import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

// A therapist's own open meetings — meetings whose time passed and whose status
// is still 'active'. No Cognito, no sidebar: the token in the URL is the whole
// authorisation and it names exactly one therapist. Same shape as the payslip
// link, because the same people open it on the same phones.
//
// The page has one job: get each row closed in clinicui. So every row is a link,
// the oldest is first, and what the therapist has already opened is remembered
// locally — this is a list worked through over days, not in one sitting.

interface OpenMeeting {
  meeting_id: number; serial: string; day: string;
  type: string; fund: string; patient_status: string; url: string | null;
}
interface Backlog {
  therapist: string; total: number; truncated: boolean; meetings: OpenMeeting[];
}

const MON_HE = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי',
  'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const dayLabel = (d: string) => `${d.slice(8)}.${d.slice(5, 7)}.${d.slice(0, 4)}`;
const monthOf = (d: string) => `${MON_HE[Number(d.slice(5, 7)) - 1]} ${d.slice(0, 4)}`;

// Which rows this person already opened. Local to the browser and advisory only —
// the real state is the meeting's status in clinicui, and the list re-reads it on
// every load. Losing this (new phone, cleared data) costs nothing but a re-tick.
const DONE_KEY = 'open-meetings-visited';
const readDone = (): Record<string, true> => {
  try { return JSON.parse(localStorage.getItem(DONE_KEY) || '{}'); } catch { return {}; }
};

export default function OpenMeetingsReport() {
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  const [data, setData] = useState<Backlog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Record<string, true>>(readDone);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/exec/active-backlog/report`,
        { params: { token } });
      setData(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'לא הצלחנו לטעון את הרשימה.');
    }
  }, [token]);

  useEffect(() => {
    if (!token) { setError('קישור חסר.'); return; }
    load();
  }, [token, load]);

  // Re-read when the tab comes back into focus: the therapist closed a meeting in
  // the other tab, and coming back to a list that still shows it is the one thing
  // that would make them stop trusting the page.
  useEffect(() => {
    const onFocus = () => { if (token) load(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [token, load]);

  const markVisited = (id: number) => {
    const next = { ...done, [id]: true as const };
    setDone(next);
    try { localStorage.setItem(DONE_KEY, JSON.stringify(next)); } catch { /* private mode */ }
  };

  if (error) {
    return (
      <div dir="rtl" className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white border border-gray-200 rounded-xl p-6 max-w-sm text-center">
          <div className="text-gray-900 font-medium mb-1">הקישור אינו זמין</div>
          <div className="text-sm text-gray-500">{error}</div>
          <div className="text-xs text-gray-500 mt-3">לבירור אפשר לפנות אלינו.</div>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div dir="rtl" className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500 text-sm">טוען…</div>;
  }

  if (!data.total) {
    return (
      <div dir="rtl" className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white border border-gray-200 rounded-xl p-6 max-w-sm text-center">
          <div className="text-3xl mb-2">✓</div>
          <div className="text-gray-900 font-medium mb-1">{data.therapist}</div>
          <div className="text-sm text-gray-500">אין פגישות פתוחות. הכול סגור.</div>
        </div>
      </div>
    );
  }

  const left = data.meetings.filter((m) => !done[m.meeting_id]).length;
  let lastMonth = '';

  return (
    <div dir="rtl" className="min-h-screen bg-gray-50 pb-10">
      <div className="max-w-lg mx-auto px-4 pt-6 space-y-4">

        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="text-sm text-gray-500">{data.therapist}</div>
          <div className="text-xs text-gray-500 mb-4">פגישות שנשארו פתוחות</div>
          <div className="text-4xl font-bold text-gray-900 tabular-nums tracking-tight">
            {data.total}
          </div>
          <div className="text-xs text-gray-500 mt-3">
            {left === data.total ? 'טרם נפתחה אף אחת' : `נותרו ${left} שטרם נפתחו`}
          </div>
          <p className="text-xs text-gray-500 mt-4 leading-relaxed">
            אלו פגישות שהמועד שלהן עבר והסטטוס שלהן עדיין <b>אקטיב</b> — לא סומן שהתקיימו,
            לא בוטלו ולא סומנו כאי-הגעה. כל עוד הפגישה פתוחה, גם סטטוס המטופל תקוע עליה.
            לחיצה על שורה פותחת את הפגישה במערכת; אחרי הסגירה, סטטוס המטופל מתעדכן מעצמו.
          </p>
        </div>

        {data.truncated && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
            מוצגות {data.meetings.length} הפגישות הוותיקות ביותר. אחרי סגירתן, רענון הדף יביא את הבאות.
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {data.meetings.map((m) => {
            const mon = monthOf(m.day);
            const head = mon !== lastMonth ? (lastMonth = mon) : null;
            const opened = !!done[m.meeting_id];
            const row = (
              <div className={`flex items-center gap-3 px-4 py-3 border-t border-gray-100 ${opened ? 'opacity-50' : ''}`}>
                <div className="text-xs tabular-nums text-gray-500 w-20 shrink-0">{dayLabel(m.day)}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-gray-900 truncate">{m.serial}</div>
                  <div className="text-xs text-gray-500 truncate">{m.type} · {m.fund}</div>
                </div>
                <div className="text-xs text-gray-500 shrink-0">{opened ? '✓' : '‹'}</div>
              </div>
            );
            return (
              <div key={m.meeting_id}>
                {head && (
                  <div className="px-4 py-2 bg-gray-50 text-xs text-gray-500 border-t border-gray-100">{head}</div>
                )}
                {m.url ? (
                  <a href={m.url} target="_blank" rel="noreferrer" onClick={() => markVisited(m.meeting_id)}
                     className="block hover:bg-gray-50">{row}</a>
                ) : row}
              </div>
            );
          })}
        </div>

        <div className="text-xs text-gray-500 text-center">
          הרשימה מתעדכנת מהמערכת בכל טעינה.
        </div>
      </div>
    </div>
  );
}
